import {
  type Context,
  createContextKey,
  type TextMapGetter,
  type TextMapPropagator,
  type TextMapSetter,
} from '@opentelemetry/api';

const ACCOUNT_ID_KEY = createContextKey('rise.accountId');
const USER_ID_KEY = createContextKey('rise.userId');
const PARTNER_ID_KEY = createContextKey('rise.partnerId');
const PAGE_PATHNAME_KEY = createContextKey('rise.pagePathname');
const PAGE_REFERER_KEY = createContextKey('rise.pageReferer');

const HEADER_ACCOUNT_ID = 'x-account-id';
const HEADER_USER_ID = 'x-user-id';
const HEADER_PARTNER_ID = 'x-partner-id';
const HEADER_PATHNAME = 'x-pathname';
const HEADER_REFERER = 'referer';

/**
 * Extracts user/account/partner IDs from headers and includes them in the span context.
 * This allows us to include them as span attributes via UserContextSpanProcessor by
 * reading those values from the context.
 */
class UserContextPropagator implements TextMapPropagator {
  extract(context: Context, carrier: unknown, getter: TextMapGetter): Context {
    const accountId = getter.get(carrier, HEADER_ACCOUNT_ID);
    const userId = getter.get(carrier, HEADER_USER_ID);
    const partnerId = getter.get(carrier, HEADER_PARTNER_ID);

    let ctx = context;

    const accountIdValue = Array.isArray(accountId) ? accountId[0] : accountId;
    if (accountIdValue) ctx = ctx.setValue(ACCOUNT_ID_KEY, accountIdValue);

    const userIdValue = Array.isArray(userId) ? userId[0] : userId;
    if (userIdValue) ctx = ctx.setValue(USER_ID_KEY, userIdValue);

    const partnerIdValue = Array.isArray(partnerId) ? partnerId[0] : partnerId;
    if (partnerIdValue) ctx = ctx.setValue(PARTNER_ID_KEY, partnerIdValue);

    const pathname = getter.get(carrier, HEADER_PATHNAME);
    const pathnameValue = Array.isArray(pathname) ? pathname[0] : pathname;
    if (pathnameValue) ctx = ctx.setValue(PAGE_PATHNAME_KEY, pathnameValue);

    const referer = getter.get(carrier, HEADER_REFERER);
    const refererValue = Array.isArray(referer) ? referer[0] : referer;
    if (refererValue) ctx = ctx.setValue(PAGE_REFERER_KEY, refererValue);

    return ctx;
  }

  inject(_context: Context, _carrier: unknown, _setter: TextMapSetter): void {
    // No-op: user/account/partner IDs are only ever extracted by this propagator so
    // that they can be included in span attributes, so we don't want to inject them
    // into outgoing requests
  }

  fields(): string[] {
    return [HEADER_ACCOUNT_ID, HEADER_USER_ID, HEADER_PARTNER_ID, HEADER_PATHNAME, HEADER_REFERER];
  }
}

const userContextPropagator = new UserContextPropagator();

export {
  ACCOUNT_ID_KEY,
  PAGE_PATHNAME_KEY,
  PAGE_REFERER_KEY,
  PARTNER_ID_KEY,
  USER_ID_KEY,
  userContextPropagator,
};
